/**
 * Check if the current window is running inside an iframe.
 *
 * @returns true if running in an iframe, false otherwise
 */
export function isInIframe(): boolean {
    if (typeof window === "undefined") {
        return false;
    }

    try {
        return window.self !== window.top;
    } catch (error) {
        // Accessing window.top can throw in cross-origin iframes; assume we are embedded.
        return true;
    }
}

/**
 * Best-effort UA check for iPad hardware, including iPadOS 13+ Safari (which
 * masquerades as "Macintosh" in `navigator.platform` but, unlike a real Mac,
 * exposes multi-touch support via `navigator.maxTouchPoints`).
 *
 * Used to gate touch/width-based auto-detection heuristics (e.g. the tablet
 * shell) so real desktop/laptop hardware with a touchscreen — e.g. Surface
 * Pro-class 2-in-1s, which `(pointer: coarse)` can misclassify even with an
 * external keyboard/mouse attached (Chromium issue 40277167) — can't be
 * swapped into an iPad-only experience.
 */
export function isIPadUserAgent(): boolean {
    if (typeof navigator === "undefined") {
        return false;
    }
    if (/iPad/.test(navigator.userAgent)) {
        return true;
    }
    return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

/** Detects Apple platforms (Mac/iPhone/iPad) for platform-specific keyboard shortcut labels. */
export function isMacPlatform(): boolean {
    return /Mac|iPhone|iPad/.test(navigator.platform ?? "");
}

/**
 * Convert basic HTML to plain text, preserving paragraph breaks.
 * Strips all tags except converts <p>, </p>, <br> to line breaks.
 */
export function htmlToText(html: string): string {
    return html
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>\s*<p[^>]*>/gi, "\n\n")
        .replace(/<\/?p[^>]*>/gi, "")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .trim();
}
